import { useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from '@/services/auth';
import logger from '@/utils/logger';

export interface LogEntry {
  tier: string;
  level: string;
  message: string;
  category?: string;
  metadata?: any;
  timestamp: string;
  username?: string;
}

interface UseLogStreamOptions {
  enabled: boolean;
  level: string;
  categories: string;
  onLog?: (log: LogEntry) => void;
}

interface UseLogStreamReturn {
  isConnected: boolean;
  connectionError: string | null;
  reconnect: () => void;
}

/**
 * Hook for consuming Server-Sent Events log stream from the backend.
 * Handles connection lifecycle and authentication.
 */
export function useLogStream({
  enabled,
  level,
  categories,
  onLog
}: UseLogStreamOptions): UseLogStreamReturn {
  const { user } = useAuth();
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const connect = useCallback(() => {
    // Clean up any existing connection
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }

    if (!enabled) {
      setIsConnected(false);
      return;
    }

    // Check if user is admin (cookie auth will be validated server-side)
    const isAdmin = user?.email?.endsWith('@206mp.com');
    if (!isAdmin) {
      setConnectionError('Admin access required');
      setIsConnected(false);
      return;
    }

    // Build SSE URL with filter parameters only
    // Auth is handled via HttpOnly cookie (set on login, sent automatically with withCredentials)
    // Use relative URL to go through nginx (prod) or Vite dev proxy (dev) - same pattern as GraphQL/axios
    const params = new URLSearchParams();
    if (level && level !== 'all') {
      params.append('level', level);
    }
    if (categories) {
      params.append('categories', categories);
    }

    const queryString = params.toString();
    const streamUrl = queryString ? `/api/logs/stream?${queryString}` : '/api/logs/stream';

    logger.info('LogStream', 'Connecting to SSE endpoint', { streamUrl });

    try {
      // Create EventSource with credentials to send HttpOnly auth cookie
      // Cookie is set on login and cleared on logout
      const eventSource = new EventSource(streamUrl, {
        withCredentials: true
      } as EventSourceInit);

      eventSource.onopen = () => {
        logger.info('LogStream', 'SSE connection established');
        setIsConnected(true);
        setConnectionError(null);
      };

      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          // Handle connection message
          if (data.type === 'connection') {
            logger.info('LogStream', 'Received connection message', data);
            return;
          }

          // Process log entry
          if (onLog) {
            onLog(data as LogEntry);
          }
        } catch (error) {
          logger.error('LogStream', 'Failed to parse SSE message', error);
        }
      };

      eventSource.onerror = (error) => {
        logger.error('LogStream', 'SSE connection error', error);
        setIsConnected(false);
        setConnectionError('Connection lost');

        // Close the connection
        eventSource.close();
        eventSourceRef.current = null;

        // Attempt reconnect after 5 seconds
        if (enabled) {
          reconnectTimeoutRef.current = setTimeout(() => {
            logger.info('LogStream', 'Attempting reconnect...');
            connect();
          }, 5000);
        }
      };

      eventSourceRef.current = eventSource;
    } catch (error) {
      logger.error('LogStream', 'Failed to create EventSource', error);
      setConnectionError('Failed to establish connection');
      setIsConnected(false);
    }
  }, [enabled, user, level, categories, onLog]);

  // Connect/disconnect based on enabled state
  useEffect(() => {
    connect();

    return () => {
      // Cleanup on unmount or when dependencies change
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      setIsConnected(false);
    };
  }, [connect]);

  const reconnect = useCallback(() => {
    logger.info('LogStream', 'Manual reconnect requested');
    connect();
  }, [connect]);

  return {
    isConnected,
    connectionError,
    reconnect
  };
}